import { useState, useEffect, useTransition } from "react";
import { useApi } from "../../api/ApiProvider";
import Api from "../../axiosApi/api";
import WeekPickerDialog from "../Dialog/WeekPickerDialog";
import DayPickerDialog from "../Dialog/DayPickerDialog";
import {
  Project,
  Timesheet,
  Task,
} from "../../axiosApi/models";
import { add, addDays, addWeeks, format, isMonday, nextFriday, nextSaturday, nextThursday, nextTuesday, nextWednesday, previousMonday, previousSunday, set, startOfDay } from "date-fns";
import { convertDateAsUTC2, durationMinutesToTime, toHoursAndMinutes } from "common/utils";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";
import { getUserEntity } from "common/UserEntityProvider";
import { PermissionsGate } from "components/authorization/PermissionsGate";
import { SCOPES } from "common/permissions";
import intl from "components/i18n/ReactIntlWrapper";
import Spinner from "components/Spinner";
import {
  Box,
  Typography,
  Button,
  Grid,
  TableRow,
  TableCell,
  Table,
  TableHead,
  TableBody,
  IconButton,
  Skeleton,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Divider,
  useMediaQuery,
  useTheme,
  Card,
} from "@mui/material";
import Title from "components/common/Title";
import { Delete, Edit, Schedule, Add, CopyAll } from "@mui/icons-material";
import { GLOBAL_DATE_CONFIG } from "common/constants";
import AutocompleteStored from "components/common/AutocompleteStored";
import { theme } from "theme/create-theme";
import TimesheetEditModal from "./TimesheetEditModal";
import { UUID } from "crypto";
import { useDialog } from "common/DialogProvider";
import TimesheetMobileComponent from "./TimesheetMobileComponent";
import SimpleBar from "simplebar-react";

export enum Day {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
}

export const DaysOfWeek: Day[] = [
  Day.MONDAY,
  Day.TUESDAY,
  Day.WEDNESDAY,
  Day.THURSDAY,
  Day.FRIDAY,
  Day.SATURDAY,
  Day.SUNDAY,
];

export type WeekDay = {
  day: string;
  duration: number;
  color: string;
}

type TimesheetRowCompoundKey = {
  idProject?: number;
  idTask?: number;
  uuId?: UUID
}
type TimesheetRowDays = {
  [K in Day]?: Timesheet | null;
}

type TimesheetRow = TimesheetRowDays & {
  totalDuration?: number; // Optional property to store total duration for the row
  project?: Project;
  task?: Task;
}

export type TimesheetWeekSet = [TimesheetRowCompoundKey, TimesheetRow];

const NON_WORKING_TYPE = "NonWorkingDay";

export const TimesheetComponent = () => {
  const mondayOfCurrentWeek = isMonday(new Date()) ? new Date() : previousMonday(new Date());
  const [date, setDate] = useState(mondayOfCurrentWeek);
  const [timesheetSet, setTimesheetSet] = useState<TimesheetWeekSet[]>([]);
  const [nonWorkingDayDates, setNonWorkingDayDates] = useState<{ date: Date }[]>([]);
  // New constant for minimum number of rows
  const MIN_ROWS = 4;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isTimesheetLoading, startTimesheetTransition] = useTransition();
  const { timesheetApi, timeoffApi }: Api = useApi();

  const userDetails = getUserEntity();
  const toast = useToastMessageQueue();
  const { openDialog } = useDialog();
  
  const fetchEvents = async () => {
    const start = new Date("1970-01-01T00:00:00");
    const end = new Date("2070-01-01T00:00:00");
    await timeoffApi.apiVversionEventAllGet("1", start, end).then((response) => {
      const renamedEvents = response?.data?.data?.queryResult?.map((event) => ({
        ...event,
        start: new Date(event.from),
        end: new Date(event.to)
      }));

      setNonWorkingDayDates(renamedEvents.filter((event) => event.eventType === NON_WORKING_TYPE).map((event) => ({
        date: event.start,
      })));

    }).catch((error) => {
      console.error("Error fetching events:", error);
    });
  };

  function getMonday(d: Date) {
    d = new Date(d);
    var day = d.getDay(),
      diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  const fetchTimesheets = async (idEmployee: number, date = new Date()) => {
    const weekStart = getMonday(date);
    const weekEnd = addDays(weekStart, GLOBAL_DATE_CONFIG.WORKING_WEEK_DAYS - 1);
    
    // Format dates as local date strings to avoid timezone conversion issues
    const weekStartStr = format(weekStart, 'yyyy-MM-dd');
    const weekEndStr = format(weekEnd, 'yyyy-MM-dd');
    
    startTimesheetTransition(async () => {
      if (!idEmployee) return;
      const response = await timesheetApi.apiVversionTimesheetGetByEmployeeIdAndDateRangeIdEmployeeFromDateToDateGet(
        idEmployee,
        weekStartStr as any, // API expects Date but we send string to avoid timezone issues
        weekEndStr as any,   // API expects Date but we send string to avoid timezone issues
        '1',
        {}
      );

      if (response.data) {
        const { data } = response.data;

        const map = new Map<TimesheetRowCompoundKey, TimesheetRow>();

        const monday = format(addDays(date, 0), 'yyyy-MM-dd');
        const tuesday = format(addDays(date, 1), 'yyyy-MM-dd');
        const wednesday = format(addDays(date, 2), 'yyyy-MM-dd');
        const thursday = format(addDays(date, 3), 'yyyy-MM-dd');
        const friday = format(addDays(date, 4), 'yyyy-MM-dd');
        const saturday = format(addDays(date, 5), 'yyyy-MM-dd');
        const sunday = format(addDays(date, 6), 'yyyy-MM-dd');

        // Process API data
        Object.keys(data.timesheetsGroupedByTaskAndDate).map(idTask => {

          const timeSheet = data.timesheetsGroupedByTaskAndDate[idTask];
          const key: TimesheetRowCompoundKey = {
            idProject: timeSheet.idProject,
            idTask: parseInt(idTask),
          }
          const timesheetRow: TimesheetRow = map.get(key) || {
            [Day.SUNDAY]: null,
            [Day.MONDAY]: null,
            [Day.TUESDAY]: null,
            [Day.WEDNESDAY]: null,
            [Day.THURSDAY]: null,
            [Day.FRIDAY]: null,
            [Day.SATURDAY]: null,
          };

          timesheetRow.project = timeSheet.project;
          timesheetRow.task = timeSheet.task;

          //get current date and current week dates
          //set timesheetRow for each day of the week
          timesheetRow[Day.MONDAY] = timeSheet.timesheetsGroupedByDate[monday] || timesheetRow[Day.MONDAY];
          timesheetRow[Day.TUESDAY] = timeSheet.timesheetsGroupedByDate[tuesday] || timesheetRow[Day.TUESDAY];
          timesheetRow[Day.WEDNESDAY] = timeSheet.timesheetsGroupedByDate[wednesday] || timesheetRow[Day.WEDNESDAY];
          timesheetRow[Day.THURSDAY] = timeSheet.timesheetsGroupedByDate[thursday] || timesheetRow[Day.THURSDAY];
          timesheetRow[Day.FRIDAY] = timeSheet.timesheetsGroupedByDate[friday] || timesheetRow[Day.FRIDAY];
          timesheetRow[Day.SATURDAY] = timeSheet.timesheetsGroupedByDate[saturday] || timesheetRow[Day.SATURDAY];
          timesheetRow[Day.SUNDAY] = timeSheet.timesheetsGroupedByDate[sunday] || timesheetRow[Day.SUNDAY];
          timesheetRow.totalDuration = 0;
          // loop Day enum and set timesheetRow for each day of the week
          Array.from(Object.values(Day)).forEach((day) => {
            const currentDay: Timesheet | null = timesheetRow[day];
            timesheetRow.totalDuration += currentDay?.duration || 0;
          });
          //set timesheetRow in the map
          map.set(key, timesheetRow);

        })

        // Preserve existing rows with UUID
        const preservedRows: TimesheetWeekSet[] = [];

        // Find and preserve rows with UUID from current state
        if (timesheetSet.length > 0) {
          timesheetSet.forEach(([key, value]) => {
            if (key.uuId) {
              // Only include if this row doesn't have a task that now exists in the data
              const rowExists = Array.from(map.keys()).some(mapKey =>
                mapKey.idTask === key.idTask && key.idTask !== undefined
              );

              if (!rowExists) {
                preservedRows.push([key, value]);
              }
            }
          });
        }

        // Calculate how many empty rows to add
        const existingRowCount = map.size + preservedRows.length;
        // Create empty rows if needed (only when not preserving rows or need more to meet MIN_ROWS)
        const defaultRows: TimesheetWeekSet[] = getEmptyRows(existingRowCount);

        setTimesheetSet([...Array.from(map.entries()), ...preservedRows, ...defaultRows]);
      }
    })
  }

  const getEmptyRows = (existingRowCount: number): TimesheetWeekSet[] => {
    // Calculate how many empty rows to add
    const emptyRowsToAdd = Math.max(MIN_ROWS - existingRowCount, 0);

    // Create empty rows if needed (only when not preserving rows or need more to meet MIN_ROWS)
    const defaultRows: TimesheetWeekSet[] = Array.from({ length: emptyRowsToAdd }, (_, i) => {
      const key: TimesheetRowCompoundKey = {
        idProject: undefined,
        idTask: undefined,
        uuId: crypto.randomUUID()
      };
      const row: TimesheetRow = {
        [Day.SUNDAY]: null,
        [Day.MONDAY]: null,
        [Day.TUESDAY]: null,
        [Day.WEDNESDAY]: null,
        [Day.THURSDAY]: null,
        [Day.FRIDAY]: null,
        [Day.SATURDAY]: null,
        totalDuration: 0
      };
      return [key, row];
    });

    return defaultRows;
  }

  const totalDurationByDate = (day: Day): number => {
    let totalDuration = 0;
    timesheetSet.forEach(([key, value]) => {
      const currentDay: Timesheet | null = value[day];
      if (currentDay && currentDay.duration) {
        totalDuration += currentDay.duration;
      }
    });
    return totalDuration;
  };

  const weekDays: WeekDay[] = DaysOfWeek.map((day: Day, i) => {
    let isNonWorkingDay = false;
    nonWorkingDayDates.forEach((nonWorkingDate) => {
      const selectedDate = addDays(date, i);
      if (
        nonWorkingDate?.date?.getDate() === selectedDate.getDate() &&
        nonWorkingDate?.date?.getMonth() === selectedDate.getMonth()
      ) {
        isNonWorkingDay = true;
      }
    });
    const isWeekend = i >= 5;
    const color = isWeekend ? "#6ea8fe" : (isNonWorkingDay ? "#ffc107" : "");
    return {
      day: format(addDays(date, i), "E, dd LLL"),
      duration: totalDurationByDate(day),
      color,
    };
  });

  const refetchTimesheetSet = async () => {
    await fetchTimesheets(userDetails?.entityId, date);
  }

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    if (date) {
      fetchTimesheets(userDetails?.entityId, date);
    }
  }, [date]);

  const totalWeekDuration = timesheetSet?.reduce((acc, [key, value]) => {
    return acc + (value.totalDuration || 0);
  }, 0)

  // Add these new handler functions for project and task selection
  const handleProjectChange = (key: TimesheetRowCompoundKey, project: Project | null) => {
    // Only handle new records (those with UUID)
    if (!key.uuId) return;

    setTimesheetSet(prevSet =>
      prevSet.map(([rowKey, rowValue]) => {
        // Find the matching row by UUID
        if (rowKey.uuId === key.uuId) {
          // Update the row with the selected project
          return [
            { ...rowKey, idProject: project?.id },
            { ...rowValue, project: project }
          ];
        }
        return [rowKey, rowValue];
      })
    );
  };

  const handleTaskChange = (key: TimesheetRowCompoundKey, task: Task | null) => {
    // Only handle new records (those with UUID)
    if (!key.uuId) return;

    // Check for duplicate task before updating
    const taskExists = timesheetSet.some(([existingKey, existingValue]) => {
      // Skip the current row in the comparison
      if (existingKey.uuId === key.uuId) return false;

      // Check if the task already exists in another row
      return existingKey.idTask === task?.id;
    });

    // If task already exists in another row, show an error message and don't update
    if (taskExists) {
      toast.error({
        message: intl.get("timesheetComponent.error.duplicateTask") || "This task is already assigned to another row",
      });
      return;
    }

    // If no duplicate, proceed with the update
    setTimesheetSet(prevSet =>
      prevSet.map(([rowKey, rowValue]) => {
        // Find the matching row by UUID
        if (rowKey.uuId === key.uuId) {
          // Update the row with the selected task
          return [
            { ...rowKey, idTask: task?.id },
            { ...rowValue, task: task }
          ];
        }
        return [rowKey, rowValue];
      })
    );
  };

  // Create a function to get all used task IDs (except for the current row)
  const getUsedTaskIds = (currentUuid?: UUID): number[] => {
    return timesheetSet
      .filter(([rowKey]) => rowKey.uuId !== currentUuid && rowKey.idTask !== undefined)
      .map(([rowKey]) => rowKey.idTask);
  };

  const handleDeleteRow = (key: TimesheetRowCompoundKey, row: TimesheetRow) => {
    openDialog({
      type: "confirm",
      title: intl.get('delete.modal.title'),
      message: 'All timesheet logs on this row will be deleted. Are you sure you want to perform this action?',
      confirmButton: {
        handleConfirm: async (message) => {
          await deleteTimesheetRow(key, row);
          message.handleClose(message);
        }
      },
      cancelButton: {
        handleCancel: (message) => {
          message.handleClose(message);
        }
      },
    });
  }

  // Add a function to add a new row  // Add deleteRow handler function
  const deleteTimesheetRow = async (key: TimesheetRowCompoundKey, row: TimesheetRow) => {
    // For rows without task/project assigned yet, just remove them from state
    if (key.uuId && (!key.idTask || !key.idProject)) {
      setTimesheetSet(prevSet => prevSet.filter(([rowKey]) => rowKey.uuId !== key.uuId));
      return;
    }

    // For rows with existing timesheet entries
    try {
      // Collect IDs of all timesheet entries in the row
      const timesheetIds: number[] = [];

      // Loop through all days to collect timesheet IDs
      Object.values(Day).forEach((day) => {
        const timesheet = row[day];
        if (timesheet && timesheet.id) {
          timesheetIds.push(timesheet.id);
        }
      });

      // Only call API if we have timesheet IDs to delete
      if (timesheetIds.length > 0) {
        await timesheetApi.apiVversionTimesheetMultipleDelete('1', timesheetIds, {});
        toast.success({
          message: intl.get("timesheetComponent.delete.success") || "Timesheet entries deleted successfully",
        });
      }

      const currentTimesheetSet = timesheetSet.filter(([rowKey]) => {
        if (key.uuId) {
          return rowKey.uuId !== key.uuId;
        }
        return !(rowKey.idProject === key.idProject && rowKey.idTask === key.idTask);
      })
      const defaultRows: TimesheetWeekSet[] = getEmptyRows(currentTimesheetSet.length);
      setTimesheetSet([...currentTimesheetSet, ...defaultRows]);

    } catch (error) {
      console.error("Error deleting timesheet entries:", error);
      toast.error({
        message: intl.get("timesheetComponent.delete.error") || "Failed to delete timesheet entries",
      });
    }
  };

  const getDisabledDeleteRow = (key: TimesheetRowCompoundKey, row: TimesheetRow): boolean => {
    // Don't allow deletion of rows if timesheet is loading
    if (isTimesheetLoading) {
      return true;
    }
    // If row has a Task assigned, and have some hours in any day, allow deletion
    if (key.idTask && row.totalDuration > 0) {
      return false; // Allow deletion if there are hours recorded
    }
    return true; // Allow deletion for all other cases
  }

  const copyTasksFromPreviousWeek = async () => {
    startTimesheetTransition(async () => {
      try {
        // Get the start of the previous week (Monday)
        const previousMondayStart = addWeeks(date, -1);
        const response = await timesheetApi.apiVversionTimesheetGetByEmployeeIdAndDateRangeIdEmployeeFromDateToDateGet(
          userDetails?.entityId,
          previousMondayStart,
          addDays(previousMondayStart, GLOBAL_DATE_CONFIG.WORKING_WEEK_DAYS - 1),
          '1',
          {}
        );

        if (response.data) {
          const { data } = response.data;
          // Create a map of tasks from the previous week
          const previousWeekTasks: Map<number, TimesheetRow> = new Map();
          Object.keys(data.timesheetsGroupedByTaskAndDate).forEach(idTask => {
            const timeSheet = data.timesheetsGroupedByTaskAndDate[idTask];
            const key: TimesheetRowCompoundKey = {
              idProject: timeSheet.idProject,
              idTask: parseInt(idTask),
            };
            const timesheetRow: TimesheetRow = {
              [Day.MONDAY]: timeSheet.timesheetsGroupedByDate[format(addDays(previousMondayStart, 0), 'yyyy-MM-dd')],
              [Day.TUESDAY]: timeSheet.timesheetsGroupedByDate[format(addDays(previousMondayStart, 1), 'yyyy-MM-dd')],
              [Day.WEDNESDAY]: timeSheet.timesheetsGroupedByDate[format(addDays(previousMondayStart, 2), 'yyyy-MM-dd')],
              [Day.THURSDAY]: timeSheet.timesheetsGroupedByDate[format(addDays(previousMondayStart, 3), 'yyyy-MM-dd')],
              [Day.FRIDAY]: timeSheet.timesheetsGroupedByDate[format(addDays(previousMondayStart, 4), 'yyyy-MM-dd')],
              [Day.SATURDAY]: timeSheet.timesheetsGroupedByDate[format(addDays(previousMondayStart, 5), 'yyyy-MM-dd')],
              [Day.SUNDAY]: timeSheet.timesheetsGroupedByDate[format(addDays(previousMondayStart, 6), 'yyyy-MM-dd')],
              totalDuration: 0,
              project: timeSheet.project,
              task: timeSheet.task
            };
            // Calculate total duration for the row
            timesheetRow.totalDuration = 0;
            Object.values(Day).forEach((day) => {
              const currentDay: Timesheet | null = timesheetRow[day];
              timesheetRow.totalDuration += currentDay?.duration || 0;
            });
            // Add to the map, overwriting if already exists
            // This ensures that if the same task exists in multiple days, it will be overwritten
            previousWeekTasks.set(key.idTask, timesheetRow);
          });
          //Get current week tasks to avoid duplicates
          const currentWeekTaskIds = new Set<number>();
          timesheetSet.forEach(([key, value]) => {
            if (key.idTask) {
              currentWeekTaskIds.add(key.idTask);
            }
          });

          // Add tasks to the current week
          const newRows: TimesheetWeekSet[] = [];
          previousWeekTasks.forEach((row, idTask) => {
            // Skip if task already exists in current week
            if (!currentWeekTaskIds.has(idTask)) {
              const newKey: TimesheetRowCompoundKey = {
                idProject: row.project?.id,
                idTask: idTask,
                uuId: crypto.randomUUID()
              };
              const newRow: TimesheetRow = {
                [Day.MONDAY]: null,
                [Day.TUESDAY]: null,
                [Day.WEDNESDAY]: null,
                [Day.THURSDAY]: null,
                [Day.FRIDAY]: null,
                [Day.SATURDAY]: null,
                [Day.SUNDAY]: null,
                totalDuration: 0,
                project: row.project,
                task: row.task
              };
              newRows.push([newKey, newRow]);
            }

          });

          // Combine existing timesheetSet with new rows
          const currentTimesheetSet = timesheetSet.filter(([key]) => !key.uuId);
          const defaultRows: TimesheetWeekSet[] = getEmptyRows(currentTimesheetSet.length + newRows.length);

          setTimesheetSet([...currentTimesheetSet, ...newRows, ...defaultRows]);
        }
      } catch (error) {
        console.error("Error copying tasks from previous week:", error);
        toast.error({
          message: intl.get("timesheetComponent.copy.error") || "Failed to copy tasks from previous week",
        });
      }
    });

  }

  const addNewRow = () => {
    const newRowKey: TimesheetRowCompoundKey = {
      idProject: undefined,
      idTask: undefined,
      uuId: crypto.randomUUID()
    };
    const newRowValue: TimesheetRow = {
      [Day.SUNDAY]: null,
      [Day.MONDAY]: null,
      [Day.TUESDAY]: null,
      [Day.WEDNESDAY]: null,
      [Day.THURSDAY]: null,
      [Day.FRIDAY]: null,
      [Day.SATURDAY]: null,
      totalDuration: 0
    };
    setTimesheetSet([...timesheetSet, [newRowKey, newRowValue]]);
  };

  //day selected to show on mobile view, default is the first day of the week (monday)
  const [selectedDay, setSelectedDay] = useState<number>(0);
  const handleDaySelect = (dayIndex: number) => {
    setSelectedDay(dayIndex);
  };
  const nextDay = () => {
    if (selectedDay === GLOBAL_DATE_CONFIG.WORKING_WEEK_DAYS - 1) {
      // If we're at the last day (Sunday), move to next week and select first day (Monday)
      const nextWeekDate = addWeeks(date, 1);
      setTimesheetSet([]);
      setDate(nextWeekDate);
      setSelectedDay(0);
    } else {
      // Move to next day within the same week
      setSelectedDay((prev) => prev + 1);
    }
  };
  const previousDay = () => {
    if (selectedDay === 0) {
      // If we're at the first day (Monday), move to previous week and select last day (Sunday)
      const previousWeekDate = addWeeks(date, -1);
      setTimesheetSet([]);
      setDate(previousWeekDate);
      setSelectedDay(GLOBAL_DATE_CONFIG.WORKING_WEEK_DAYS - 1);
    } else {
      // Move to previous day within the same week
      setSelectedDay((prev) => prev - 1);
    }
  };

  const handleDateChange = (newDate: Date) => {
    setTimesheetSet([]);
    setDate(newDate);
  };

  if (isMobile) {
    // Return early for mobile view to avoid rendering desktop components
    return (
      <Box sx={{ height: '100%', display: 'flex', flexGrow: 'inherit', flexDirection: 'column' }}>
        {/* Mobile view - only visible on mobile devices */}
        <Grid container spacing={2} p={2}>
          <Grid size={12}>
            <DayPickerDialog
              date={date}
              isTimesheetLoading={isTimesheetLoading}
              onPreviousDay={previousDay}
              onNextDay={nextDay}
              onDateChange={handleDateChange}
            />
          </Grid>
        </Grid>
        <Box id="day-view" sx={{ height: '100%', display: 'flex', flexGrow: 'inherit', flexDirection: 'column' }}>


          <TimesheetMobileComponent
            weekDays={weekDays}
            selectedDay={selectedDay}
            totalWeekDuration={totalWeekDuration}
            timesheetSet={timesheetSet}
            date={date}
            isTimesheetLoading={isTimesheetLoading}
            handleProjectChange={handleProjectChange}
            handleTaskChange={handleTaskChange}
            addNewRow={addNewRow}
            refetchTimesheetSet={refetchTimesheetSet}
          />

        </Box>
        <Box sx={{ display: 'flex', p: 2, gap: 1 }} >
          <Button
            variant="contained"
            color="primary"
            onClick={addNewRow}
            disabled={isTimesheetLoading}
            fullWidth
          >
            {intl.get("timesheetComponent.add.button")}
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={copyTasksFromPreviousWeek}
            disabled={isTimesheetLoading}
            startIcon={<CopyAll />}
            fullWidth
          >
            {intl.get('timesheetComponent.copy.button')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        {isTimesheetLoading &&
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
          </Box>
        }
        <PermissionsGate
          viewScopes={[SCOPES["timesheet.read"]]}
          editScopes={[SCOPES["timesheet.edit"]]}
          viewRoles={[]}
          editRoles={[]}
          RenderError={() => <span>{intl.get("permissionsGate")}</span>}
        >
          <Title>{intl.get('users.header')}</Title>
          <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }} display={"flex"} alignItems="center" gap={1}>
            <Schedule />
            {intl.get("timesheetComponent.header")}
          </Typography>

          <Grid container spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Grid size={12}>
              <WeekPickerDialog
                selectedDate={date}
                handleDateChange={(newDate) => {
                  setTimesheetSet([]);
                  setDate(newDate);
                }}
              />
            </Grid>
          </Grid>
          {/* Mobile view - only visible on mobile devices */}


          {/* Desktop view - only visible on tablet and larger */}
          <Grid container spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Grid size={12}>
              <Table>
                <TableHead>
                  <TableRow sx={{ borderBottom: theme.palette.divider }}>
                    <TableCell sx={{ verticalAlign: "bottom", minWidth: "200px", backgroundColor: theme.palette.background.default }}>
                      {intl.get("timesheetTable.clientProject")}
                    </TableCell>
                    <TableCell align="center" sx={{ verticalAlign: "bottom", minWidth: "200px", backgroundColor: theme.palette.background.default }}>
                      {intl.get("timesheetTable.task")}
                    </TableCell>
                    {weekDays?.map((day, i) => (
                      <TableCell key={`ts_h_${i}`} sx={{ verticalAlign: "bottom", backgroundColor: theme.palette.background.default }}>
                        <Box display={"flex"} flexDirection={"column"} alignItems={"end"}>
                          {isTimesheetLoading ?
                            <>
                              <Skeleton variant="text" width="100px" animation="pulse" />
                              <Skeleton variant="text" width="50px" animation="pulse" />
                            </>
                            :
                            <>
                              <Typography variant="caption" color="textSecondary">
                                {day.day}
                              </Typography>
                              <Typography variant="h6" color="textSecondary" fontWeight={600} style={{ color: day.color }}>
                                {durationMinutesToTime(day.duration)}
                              </Typography>
                            </>
                          }
                        </Box>
                      </TableCell>
                    ))}
                    <TableCell sx={{ verticalAlign: "bottom", minWidth: "150px", backgroundColor: theme.palette.background.default }}>
                      <Box display={"flex"} flexDirection={"column"} alignItems={"end"}>
                        <Typography variant="h5" color="textSecondary" sx={{ fontWeight: "bold" }}>
                          {intl.get("timesheetTable.total")}
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="textSecondary">
                          {durationMinutesToTime(totalWeekDuration)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timesheetSet?.map(([key, timesheetRow]) => (
                    <TableRow key={key.uuId ? key.uuId : `${key.idProject}-${key.idTask}`}>
                      <TableCell padding="none" sx={{ px: .5 }}>
                        <AutocompleteStored
                          queryType='assignedProjects'
                          queryParams={{ idEmployee: userDetails?.entityId, endDate: date }}
                          groupBy={(option: Project) => option?.client?.name || ''}
                          getOptionKey={(option => option?.id)}
                          getOptionLabel={(option: Project) => `${option?.name}`}
                          renderValue={(option: Project) => <Box display={"flex"} alignItems="center" sx={{}}>
                            {option?.client && (
                              <Chip
                                variant="filled"
                                label={option.client.name}
                                size="small"
                              />
                            )}
                            <Typography
                              variant="h6"
                              title={option?.name}
                              sx={{ ml: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '150px' }}
                            >
                              {option?.name}
                            </Typography>
                          </Box>
                          }
                          selectOnFocus
                          value={key.idProject}
                          disabled={key.idProject !== undefined && !key.uuId}
                          disableClearable
                          fullWidth
                          onChange={(e, value) => handleProjectChange(key, value)}
                        />
                      </TableCell>
                      <TableCell padding="none" sx={{ px: .5 }}>
                        <AutocompleteStored
                          queryType='taskByEmployeeAndProject'
                          queryParams={{ idEmployee: userDetails?.entityId, idProject: key.idProject }}
                          getOptionKey={(option => option?.id)}
                          groupBy={(option: Task) => option?.job?.description || ''}
                          getOptionLabel={(option) => option?.description || ''}
                          renderValue={(option: Task) => <Box display={"flex"} alignItems="center" sx={{}}>
                            {option?.job && (
                              <Chip
                                variant="filled"
                                label={option.job.description}
                                size="small"
                              />
                            )}
                            <Typography variant="h6" title={option?.description} sx={{ ml: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '150px' }}>
                              {option?.description}
                            </Typography>
                          </Box>
                          }
                          disabled={key.idProject === undefined || (!key.uuId && key.idTask !== undefined)}
                          value={key.idTask}
                          disableClearable
                          fullWidth
                          onChange={(e, value) => handleTaskChange(key, value)}
                          filterIds={getUsedTaskIds(key.uuId)} // Filter out already used tasks
                        />
                      </TableCell>
                      {DaysOfWeek.map((day, i) => {
                        const currentDate = startOfDay(addDays(date, i));
                        const currentTimesheet: Timesheet = timesheetRow[day] || {
                          idEmployee: userDetails?.entityId,
                          idTask: key.idTask,
                          date: convertDateAsUTC2(currentDate),
                          startTime: convertDateAsUTC2(currentDate),
                          task: {
                            ...timesheetRow?.task,
                            job: {
                              ...timesheetRow?.task?.job,
                              project: timesheetRow?.project
                            }
                          },
                        };
                        return (
                          <TableCell width={150} key={`ts_r_${key.idProject}-${key.idTask}-${day}`} padding="none" sx={{ px: .5 }}>
                            {!isTimesheetLoading ?
                              <TimesheetEditModal
                                disabled={key.uuId !== undefined}
                                timesheet={currentTimesheet} onComplete={() => { refetchTimesheetSet() }} />
                              :
                              <Skeleton variant="rounded" width="100%" height={40} animation="pulse" />
                            }
                          </TableCell>
                        );
                      })}
                      <TableCell>
                        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} height={40}>
                          {isTimesheetLoading ?
                            <>
                              <Skeleton variant="circular" width={20} height={20} animation="pulse" />
                              <Skeleton variant="text" width="50px" animation="pulse" />
                            </>
                            :
                            <>
                              <IconButton
                                onClick={() => handleDeleteRow(key, timesheetRow)}
                                disabled={getDisabledDeleteRow(key, timesheetRow)}
                              >
                                <Delete />
                              </IconButton>
                              <Typography variant="h6" fontWeight={600} color="textSecondary">
                                {toHoursAndMinutes(timesheetRow.totalDuration || 0)}
                              </Typography>
                            </>
                          }
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>

            {/* Add button row */}
            <Grid size={12} display="flex" justifyContent="flex-start" mt={2} gap={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={addNewRow}
                disabled={isTimesheetLoading}
              >
                {intl.get("timesheetComponent.add.button")}
              </Button>
              <Button
                color="primary"
                variant="outlined"
                onClick={copyTasksFromPreviousWeek}
                disabled={isTimesheetLoading}
                startIcon={<CopyAll />}
              >
                {intl.get('timesheetComponent.copy.button')}
              </Button>
            </Grid>
          </Grid>

          {/* Mobile add button - only shown on mobile devices */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-start', mt: 2, gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={addNewRow}
              disabled={isTimesheetLoading}
              fullWidth
            >
              {intl.get("timesheetComponent.add.button")}
            </Button>
            <Button
              color="primary"
              variant="outlined"
              onClick={copyTasksFromPreviousWeek}
              disabled={isTimesheetLoading}
              startIcon={<CopyAll />}
              fullWidth
            >
              {intl.get('timesheetComponent.copy.button')}
            </Button>
          </Box>
        </PermissionsGate>
      </Box>
    </Card>
  );
}